import {
  AuthProvider as UtilAuthProvider,
  useAuthContext as useUtilAuthContext,
} from '@soniq/util-auth'
import { useInterval } from '@pogokid/react-hooks'
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { getAllLogger } from '~/logger.ts'
import { queueClient } from '~m/resource/queue.ts'
import { userManager } from '~m/auth/config.ts'
import {
  AuthContext,
  AuthContextProps,
  AuthProviderState,
} from './context.ts'
import { signoutCompleteReturnToPath } from '~m/auth/paths.ts'

const log = getAllLogger('ui:auth:AuthProvider')

interface AuthProviderProps {}

export const AuthProvider: FC<
  React.PropsWithChildren<AuthProviderProps>
> = ({ children }) => {
  return (
    <UtilAuthProvider userManager={userManager}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </UtilAuthProvider>
  )
}

function AuthProviderInner({ children }: PropsWithChildren) {
  const utilAuth = useUtilAuthContext()

  const [state, setState] = useState<AuthProviderState>({
    error: null,
  })

  useInterval(() => {
    if (utilAuth.isSyncingAuths || !utilAuth.authUser) return
    userManager.getUser().then((user) => {
      if (!user) {
        console.warn('Detected user signed out from another tab')
        location.href = '/faq'
      }
    })
  }, 2_000)

  useEffect(() => {
    async function userSessionChanged() {
      const user = await userManager.getUser()
      if (user && !user.expired) {
        log.verbose(
          'Session changed - start processing',
          user.profile.sub
        )
        queueClient.setState({
          accessToken: user.access_token ?? null,
          userId: user.profile.sub,
          profileId: user.profile.sub,
        })
      } else {
        log.verbose(
          'Session changed - set state',
          user?.profile.sub
        )
        void queueClient.setState({
          accessToken: null,
          userId: user?.profile.sub,
          profileId: user?.profile.sub,
        })
      }
    }
    void userSessionChanged()
    userManager.events.addUserSessionChanged(userSessionChanged)
    userManager.events.addUserLoaded(userSessionChanged)
    userManager.events.addUserUnloaded(userSessionChanged)
    async function userSignedIn() {
      void queueClient.setState({ signedOut: false })
    }
    userManager.events.addUserSignedIn(userSignedIn)

    return () => {
      userManager.events.removeUserSessionChanged(
        userSessionChanged
      )
      userManager.events.removeUserLoaded(userSessionChanged)
      userManager.events.removeUserSignedIn(userSignedIn)
    }
  }, [])

  const value = useMemo((): AuthContextProps => {
    return {
      ...state,
      ...utilAuth,
      async signOut() {
        setState((prev) => ({ ...prev, isSyncingAuths: true }))
        void utilAuth.signOut({
          returnTo: signoutCompleteReturnToPath(),
        })
      },
    }
  }, [state, utilAuth])
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
